<template>
    <b-container fluid class="py-3">
        <b-row class="mb-3">
            <b-col cols="12" class="d-flex align-items-center">
                <h5 class="mb-0">결과보고서 일괄 생성</h5>
                <span class="ml-3 text-muted">프로젝트 {{ project_code }}</span>
                <b-button class="ml-auto" variant="dark-border" size="sm" :disabled="running" @click="loadRoster">
                    새로고침
                </b-button>
            </b-col>
        </b-row>

        <b-row class="mb-2">
            <b-col cols="12">
                <b-form-checkbox v-model="do_confirm" :disabled="running">
                    S3 업로드 + 확정
                    <span class="text-danger">— 확정하면 원청 포털에 즉시 공개됩니다.</span>
                    <span class="text-muted">해제하면 서버에 아무것도 쓰지 않고 ZIP 만 받습니다.</span>
                </b-form-checkbox>
            </b-col>
        </b-row>

        <b-row v-if="running" class="mb-2">
            <b-col cols="12">
                <b-progress :max="run_total" height="1.4rem">
                    <b-progress-bar :value="run_index" :label="`${run_index} / ${run_total}`"></b-progress-bar>
                </b-progress>
                <div class="d-flex mt-1">
                    <small class="text-muted">{{ run_current_name }} — {{ phase_label }}</small>
                    <small class="ml-auto text-muted">누적 {{ formatMB(run_bytes) }}</small>
                    <b-button class="ml-3" variant="outline-danger" size="sm" @click="cancel_requested = true">
                        {{ cancel_requested ? '중단 중…' : '중단' }}
                    </b-button>
                </div>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-table :items="rows" :fields="fields" small bordered responsive :busy="loading">
                    <template #head(selected)>
                        <b-form-checkbox :checked="all_selected" :disabled="running" @change="toggleAll"></b-form-checkbox>
                    </template>
                    <template #cell(selected)="data">
                        <b-form-checkbox v-model="data.item.selected" :disabled="running"></b-form-checkbox>
                    </template>
                    <template #cell(mode)="data">
                        {{ data.item.mode === 'DOC' ? '서면평가' : '실사평가' }}
                    </template>
                    <template #cell(state)="data">
                        <b-badge v-if="data.item.already_confirmed" variant="success">이미 확정됨</b-badge>
                        <b-badge v-if="data.item.confirm_blocked" variant="warning">서면평가 확정 필요</b-badge>
                        <b-badge v-if="failed_by_company[data.item.company_id]" variant="danger">
                            {{ failed_by_company[data.item.company_id].phase }} 실패
                        </b-badge>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-button variant="dark" :disabled="running || !selected_rows.length" @click="start">
                    {{ selected_rows.length }}개 업체 실행
                </b-button>
            </b-col>
        </b-row>

        <!-- 렌더링 전용. reportData 가 준비된 뒤에만 마운트한다 — 뷰어가 mounted 에서 스스로 한 번 렌더하므로
             (ReportViewerSUPPLY.vue:123-131) null 데이터로 마운트하면 그 렌더가 실패한다.
             assessmentType 은 상수 '' 다. 업체마다 바꾸면 watcher(:92-100)가 previewPDF 를 추가로 돌린다.
             '' 이면 렌더러가 reportData.info.service_type 으로 판정한다 (report.supply.mixin.js:1825, :2165). -->
        <BulkReportContainer v-if="render_data"
                             ref="bulkContainer"
                             class="d-none"
                             :report_lang="report_lang"
                             :reportData="render_data"
                             :pdf_filename="render_filename"
                             reportMode="full"
                             assessmentType=""
                             @sendReportBlob="onViewerBlob"
        />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {mapState, mapActions} from 'vuex';
import {zipSync} from 'fflate';
import BulkReportContainer from '@shared/components/admin/components/report/ReportMamagement/BulkReportContainer.vue';
// .cjs 사이드카는 require 로 부른다 — 기존 관례(evaluations.js:2, EditingSubComponentMixin.js:10)와 동일.
const {buildRunPlan, runBulk} = require('@shared/components/admin/pages/report-bulk-run.cjs');

const PHASE_LABEL = {
    data: '평가 데이터 조회',
    render: '보고서 생성',
    validate: '검증',
    upload: '업로드',
    confirm: '확정',
};

export default {
    name: 'ReportBulkRun',
    components: {BulkReportContainer},
    data() {
        return {
            loading: false,
            running: false,
            rows: [],
            do_confirm: true,
            cancel_requested: false,

            run_index: 0,
            run_total: 0,
            run_current_name: '',
            run_phase: '',
            run_bytes: 0,
            failed_by_company: {},

            // 렌더러에 넘길 현재 업체 데이터. 컨테이너는 이 값이 생겨야 마운트된다.
            render_data: null,
            render_filename: '',
            container_ready: false,
            mount_render_resolve: null,

            ev_code_list: null,
            fields: [
                {key: 'selected', label: ''},
                {key: 'company_name', label: '업체명'},
                {key: 'mode', label: '평가유형'},
                {key: 'state', label: '상태'},
            ],
        };
    },
    computed: {
        ...mapState(['is_admin']),
        project_code() {
            return this.$route.query.pc;
        },
        report_lang() {
            return this.$i18n.locale;
        },
        selected_rows() {
            return this.rows.filter(r => r.selected);
        },
        all_selected() {
            return this.rows.length > 0 && this.rows.every(r => r.selected);
        },
        phase_label() {
            return PHASE_LABEL[this.run_phase] || '';
        },
    },
    created() {
        // 라우트 가드는 로그인만 본다(guard.js:45-49). 관리자 등급은 여기서 막는다.
        if (this.is_admin !== 4) {
            this.$router.replace({name: 'ProjectCodeList'});
            return;
        }
        if (!this.project_code) {
            this.$showModal({type: 'alert', state: 'warning', title: '프로젝트 코드가 없습니다.', message: '프로젝트 목록에서 다시 진입해주세요.'});
            return;
        }
        this.loadRoster();
    },
    beforeDestroy() {
        // 실행 중 이탈하면 다음 업체를 시작하지 않는다.
        this.cancel_requested = true;
    },
    methods: {
        ...mapActions('evaluations', ['fetchEvCode']),
        formatMB(bytes) {
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },
        toggleAll(checked) {
            this.rows.forEach(r => { r.selected = checked; });
        },
        async loadRoster() {
            this.loading = true;
            this.failed_by_company = {};
            try {
                const [_roster, _history] = await Promise.all([
                    this.$axios('/report', {params: {project_code: this.project_code}}),
                    this.$axios('/report/history', {params: {project_code: this.project_code, with_alt: true}}),
                ]);
                this.rows = buildRunPlan(_roster.data, _history.data.result);
            } catch (error) {
                this.$showModal({
                    type: 'alert', state: 'error', title: '대상 목록을 불러오지 못했습니다.',
                    message: this.errorText(error),
                });
            } finally {
                this.loading = false;
            }
        },
        errorText(error) {
            return getErrorMessage(error, '') || (error && error.message ? error.message : String(error));
        },
        onViewerBlob() {
            if (this.mount_render_resolve) {
                this.mount_render_resolve();
                this.mount_render_resolve = null;
            }
        },
        // 컨테이너가 처음 마운트되면 뷰어가 폰트(8종 TTF)를 받은 뒤 스스로 한 번 렌더하고 sendReportBlob 을 쏜다.
        // 그게 끝나기 전에 우리가 generatePDF 를 부르면 같은 캔버스·문서를 두 렌더가 나눠 쓴다.
        // 그 렌더 결과는 버린다 — 대기가 목적이고, 덤으로 폰트 로딩까지 끝난 상태가 보장된다.
        waitForMountRender() {
            return new Promise(resolve => {
                this.mount_render_resolve = resolve;
                // 마운트 렌더가 실패하면(렌더러가 예외를 삼킨다) 이벤트가 오지 않는다. 무한 대기 금지.
                setTimeout(() => {
                    if (this.mount_render_resolve) {
                        this.mount_render_resolve = null;
                        resolve();
                    }
                }, 60000);
            });
        },
        buildIo() {
            return {
                fetchData: async (row) => {
                    const _res = await this.$axios('/report/data', {
                        params: {campaign_id: row.campaign_id, project_code: row.project_code, company_id: row.company_id},
                    });
                    // 상위집합 페이로드. 레거시(ReportBulkGenerator.vue:663-680)는 렌더러 판정에 따라 두 가지 모양을
                    // 따로 만들었는데, 그 판정이 컨테이너의 판정과 달라(fallbackRenderer 유무) 어긋나면 백지가 나왔다.
                    // 전부 넘기면 판정이 필요 없다 — 어느 렌더러든 자기가 읽는 것만 읽는다.
                    const _data = {..._res.data, parser: _res.data.parser_result, ev_code_list: this.ev_code_list};

                    this.render_data = _data;
                    await this.$nextTick();
                    if (!this.container_ready) {
                        await this.waitForMountRender();
                        this.container_ready = true;
                    }
                    return _data;
                },
                filename: (data, row) => {
                    // 이름 규칙은 report.naming.mixin.js:48-76 에 이미 있다. 레거시처럼 표를 복제하지 않고 그걸 읽는다.
                    const _viewer = this.$refs.bulkContainer && this.$refs.bulkContainer.$refs.resultReport;
                    const _base = _viewer && _viewer.report_filename ? _viewer.report_filename : '결과보고서';
                    const _company = data.info && data.info.company && data.info.company.company_name
                        ? data.info.company.company_name
                        : row.company_name;
                    return _company ? `${_base}_${_company}` : _base;
                },
                render: async (data, filename) => {
                    this.render_filename = filename;
                    await this.$nextTick();
                    return this.$refs.bulkContainer.generatePDF();
                },
                toBlob: (doc, filename) => {
                    const _blob = doc.output('blob', {filename: `${filename}.pdf`});
                    // ZIP 은 메모리에 전부 올라간다. 운영자가 한계에 다가가는 걸 보고 있게 누적치를 노출한다.
                    this.run_bytes += _blob.size;
                    return _blob;
                },
                upload: async (row, blob, filename) => {
                    const _form = new FormData();
                    _form.append('campaign_id', row.campaign_id);
                    _form.append('company_id', row.company_id);
                    _form.append('project_code', row.project_code);
                    _form.append('report', new File([blob], `${filename}.pdf`, {type: 'application/pdf'}));
                    _form.append('en', this.report_lang !== 'ko');
                    _form.append('mode', row.mode);

                    const _res = await this.$axios.post('/report/release', _form, {
                        headers: {'Content-Type': 'multipart/form-data'},
                    });
                    return _res.data ? _res.data.report_history_id : null;
                },
                confirm: (reportId) => this.$axios({method: 'PUT', url: '/report/confirm', data: {report_id: reportId}}),
                unconfirm: (reportId) => this.$axios({method: 'DELETE', url: '/report/confirm', data: {report_id: reportId}}),
            };
        },
        async start() {
            const _targets = this.selected_rows;
            const _blocked = _targets.filter(r => this.do_confirm && r.confirm_blocked).length;
            const _reconfirm = _targets.filter(r => this.do_confirm && r.already_confirmed).length;

            const _warn = [];
            if (this.do_confirm) {
                _warn.push(`${_targets.length}개 업체의 보고서를 생성하고 확정합니다. 확정 즉시 원청 포털에 공개됩니다.`);
                if (_reconfirm) _warn.push(`이미 확정된 ${_reconfirm}개 업체는 기존 확정이 해제되고 새 보고서로 다시 확정됩니다.`);
                if (_blocked) _warn.push(`서면평가가 확정되지 않은 실사평가 ${_blocked}개 업체는 생성·ZIP 만 하고 확정은 건너뜁니다.`);
            } else {
                _warn.push(`${_targets.length}개 업체의 보고서를 생성해 ZIP 으로만 내려받습니다. 서버에는 아무것도 쓰지 않습니다.`);
            }

            const _h = this.$createElement;
            const _ok = await this.$showModal({
                type: 'confirm',
                state: this.do_confirm ? 'warning' : 'info',
                title: '결과보고서 일괄 생성',
                message: _h('div', {}, _warn.map(t => _h('p', {class: 'mb-1'}, t))),
            });
            if (!_ok) return;

            this.running = true;
            this.cancel_requested = false;
            this.failed_by_company = {};
            this.run_index = 0;
            this.run_total = _targets.length;
            this.run_bytes = 0;
            this.container_ready = false;

            try {
                if (!this.ev_code_list) this.ev_code_list = await this.fetchEvCode();

                const _result = await runBulk(_targets, this.buildIo(), {
                    confirm: this.do_confirm,
                    cancelled: () => this.cancel_requested,
                    onProgress: (index, row, phase) => {
                        this.run_index = index;
                        this.run_current_name = row.company_name;
                        this.run_phase = phase;
                    },
                });

                this.run_index = this.run_total;
                _result.failed.forEach(f => { this.$set(this.failed_by_company, f.company_id, f); });
                await this.downloadZip(_result.files);
                await this.reportResult(_result);
            } catch (error) {
                this.$showModal({type: 'alert', state: 'error', title: '실행이 중단되었습니다.', message: this.errorText(error)});
            } finally {
                this.running = false;
                this.render_data = null;
                this.render_filename = '';
                this.container_ready = false;
                await this.loadRoster();
            }
        },
        async downloadZip(files) {
            if (!files.length) return;
            // zipSync 는 Uint8Array 만 받는다. 여기서 전부 메모리에 올라가므로 업체 수가 커지면 이 지점이 한계다.
            // ponytail: 실측 한계를 넘기면 fflate 의 AsyncZipDeflate 스트리밍으로 바꾼다. 지금은 단순함이 이긴다.
            const _entries = {};
            for (const _file of files) {
                _entries[_file.name] = new Uint8Array(await _file.blob.arrayBuffer());
            }

            const _blob = new Blob([zipSync(_entries, {level: 6})], {type: 'application/zip'});
            const _link = document.createElement('a');
            _link.href = URL.createObjectURL(_blob);
            _link.download = `결과보고서_${this.project_code}.zip`;
            document.body.appendChild(_link);
            _link.click();
            document.body.removeChild(_link);
            URL.revokeObjectURL(_link.href);
        },
        async reportResult(result) {
            const _h = this.$createElement;
            const _lines = [
                _h('p', {class: 'mb-1'}, `성공 ${result.done.length}건 / 실패 ${result.failed.length}건${result.cancelled ? ' (중단됨)' : ''}`),
                _h('p', {class: 'mb-2'}, `ZIP 에 ${result.files.length}개 보고서가 담겼습니다.`),
            ];
            result.failed.forEach(f => {
                let _text = `${f.company_name} — ${PHASE_LABEL[f.phase] || f.phase}: ${f.reason}`;
                // 업로드 뒤에 죽었으면 서버에 이력 행이 남는다. 숨기지 않고 번호를 준다.
                if (f.released_history_id) _text += ` (이력 #${f.released_history_id} 생성됨)`;
                if (f.restore_failed) _text += ' ⚠ 기존 확정 복구 실패 — 수동 확인 필요';
                _lines.push(_h('p', {class: 'mb-0 small text-danger'}, _text));
            });

            await this.$showModal({
                type: 'alert',
                state: result.failed.length ? 'warning' : 'success',
                title: '일괄 생성 완료',
                message: _h('div', {}, _lines),
            });
        },
    },
};
</script>
